import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "~/store/auth.store";
import usePurchaseStepStore from "~/store/purchase.store";
import { ArrowLeft, Check, X } from "lucide-react";
import networkService from "~/services/network.service";
import { GET_LICENSES_BY_ORG_ID, CREATE_SUBSCRIPTION, SUBSCRIBE_PAYMENT, SERVICE_ID, GET_PLANS, PROFILE_INFO_API } from "~/shared/constants/api";
import { handleError } from "~/shared/utils/errors.util";
import { UserRole, PlanId } from "~/shared/config";
import { Plan } from "~/interfaces/interfaces";
import { toast } from "react-toastify";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { LavendarChevronIcon } from "~/shared/icon/lavendar-chevron-icon";

const StepCounter = ({ step }: { step: number }) => {
  const steps = [
    { label: "Order" },
    { label: "Payment" },
    { label: "Confirmation" },
  ];
  return (
    <div className="hidden items-center justify-center gap-8 mb-14 md:flex">
      {steps.map((s, idx) => (
        <div key={s.label} className="flex items-center gap-2">
          <div
            className={`w-6 h-6 flex items-center justify-center rounded-md bg-gray-200 ${step === idx + 1 ? "text-white" : "border-gray-300 bg-white text-gray-500"}`}
          >
            {step > idx + 1 ? (
              <Check size={16} className="text-[#807CD6] font-bold" />
            ) : (
              idx + 1
            )}
          </div>
          <span className={`font-medium ml-2 ${step === idx + 1 ? "text-[#807CD6]" : "text-gray-500"}`}>{s.label}</span>
        </div>
      ))}
    </div>
  );
};

// Add this function to call the status summary API
const getOrgStatusSummary = async (orgId: string) => {
  return networkService.get<any>(`/organizations/${orgId}/status-summary`);
};

export const US_STATES = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" },
];

// Type for billing details API response
type BillingDetailsResponse = {
  data: {
    legalName?: string;
    legalNumber?: string;
    name?: string;
    email?: string;
    addressLine1?: string;
    addressLine2?: string;
    zipcode?: string;
    state?: string;
    city?: string;
  };
};

// Type for profile API response
type UserProfileResponse = {
  data: {
    email: string;
    firstName: string;
    lastName: string;
    emailVerified: boolean;
    organizations: Array<{
      organization: {
        name: string;
      };
    }>;
  };
};

interface BillingInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  user: any;
  isFirstTime: boolean; // New prop to indicate if this is first time or editing
}

function BillingInfoModal({ isOpen, onClose, onSaved, user, isFirstTime }: BillingInfoModalProps) {
  const [initialValues, setInitialValues] = useState({
    firstName: "",
    lastName: "",
    organization: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    zipcode: "",
    state: "",
    city: "",
    country: "United States",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !user?.organizationId || !user?.userId) return;

    setLoading(true);

    if (isFirstTime) {
      // First time: Fetch user profile data
      const fetchUserProfile = async () => {
        try {
          const response = await networkService.get<UserProfileResponse>(
            `${PROFILE_INFO_API}/${user.userId}`
          );
          const profileData = response.data;

          setInitialValues({
            firstName: profileData.firstName || "",
            lastName: profileData.lastName || "",
            organization: profileData.organizations?.[0]?.organization?.name || "",
            email: profileData.email || "",
            addressLine1: "",
            addressLine2: "",
            zipcode: "",
            state: "",
            city: "",
            country: "United States",
          });
        } catch (error: any) {
          handleError({
            error: error,
            message: error.message,
            level: "error"
          });
          // Fallback to user store data if API fails
          setInitialValues({
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            organization: user?.organizationName || user?.organization || "",
            email: user?.email || "",
            addressLine1: "",
            addressLine2: "",
            zipcode: "",
            state: "",
            city: "",
            country: "United States",
          });
        } finally {
          setLoading(false);
        }
      };

      fetchUserProfile();
    } else {
      // Editing: Fetch billing details
      const fetchBillingDetails = async () => {
        try {
          const response = await networkService.get<BillingDetailsResponse>(
            `/organizations/${user.organizationId}/billing-details`
          );
          const billingData = response.data;

          if (billingData && Object.keys(billingData).length > 0) {
            // Parse name into firstName and lastName
            let firstName = "";
            let lastName = "";
            if (billingData.name) {
              const nameParts = billingData.name.trim().split(" ");
              firstName = nameParts[0] || "";
              lastName = nameParts.slice(1).join(" ") || "";
            }

            // Set state dropdown value to the matching state name if code is present
            let stateValue = "";
            if (billingData.state) {
              const foundState = US_STATES.find(
                s =>
                  s.code.toLowerCase() === billingData.state.toLowerCase() ||
                  s.name.toLowerCase() === billingData.state.toLowerCase()
              );
              stateValue = foundState ? foundState.name : billingData.state;
              console.log("stateValue", stateValue);
              console.log("billingData.state", billingData.state);
            }

            setInitialValues({
              firstName: firstName || user?.firstName || "",
              lastName: lastName || user?.lastName || "",
              organization: billingData.legalName || user?.organizationName || user?.organization || "",
              email: billingData.email || user?.email || "",
              addressLine1: billingData.addressLine1 || "",
              addressLine2: billingData.addressLine2 || "",
              zipcode: billingData.zipcode || "",
              state: stateValue,
              city: billingData.city || "",
              country: "United States",
            });
          } else {
            // No billing data found, fallback to user data
            setInitialValues({
              firstName: user?.firstName || "",
              lastName: user?.lastName || "",
              organization: user?.organizationName || user?.organization || "",
              email: user?.email || "",
              addressLine1: "",
              addressLine2: "",
              zipcode: "",
              state: "",
              city: "",
              country: "United States",
            });
          }
        } catch (error: any) {
          handleError({
            error: error,
            message: error.message,
            level: "error"
          });
          // Fallback to user store data if API fails
          setInitialValues({
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            organization: user?.organizationName || user?.organization || "",
            email: user?.email || "",
            addressLine1: "",
            addressLine2: "",
            zipcode: "",
            state: "",
            city: "",
            country: "United States",
          });
        } finally {
          setLoading(false);
        }
      };

      fetchBillingDetails();
    }
  }, [isOpen, user, isFirstTime]);

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    organization: Yup.string().required("Organization name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    addressLine1: Yup.string().required("Address line 1 is required"),
    addressLine2: Yup.string(),
    zipcode: Yup.string().required("ZIP code is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    country: Yup.string().required("Country is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      await networkService.put(
        `/organizations/${user.organizationId}/billing-details`,
        {
          addressLine1: values.addressLine1,
          addressLine2: values.addressLine2,
          city: values.city,
          country: "US",
          email: values.email,
          name: `${values.firstName} ${values.lastName}`.trim(),
          legalName: values.organization,
          state: values.state,
          zipcode: values.zipcode,
        }
      );
      toast.success("Billing information updated successfully");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to update billing information");
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black-900 bg-opacity-50">
      <div className="bg-white-a700 shadow-lg relative mb-12 max-w-[600px] rounded-3xl w-full mx-4 md:mx-0">
        <div className="font-space-grotesk">
          {/* Header with close button */}
          <div className="flex justify-between items-center px-7 pb-4 pt-6">
            <span className="font-medium">
              {isFirstTime ? "Add Billing Information" : "Edit Billing Information"}
            </span>
            <button onClick={onClose} className="text-2xl">×</button>
          </div>
          <div className="w-full h-px bg-gray-200"></div>
          <div className="px-7 py-5">
            <h2 className="text-3xl font-bold mb-5">
              Set Up Your Billing Information
            </h2>
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <div className="text-gray-600">Loading...</div>
              </div>
            ) : (
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="flex flex-col gap-4 h-[500px] overflow-y-auto md:h-auto">
                    <div className="flex gap-4 flex-col md:flex-row">
                      <div className="flex-1">
                        <label className="block mb-1">First Name</label>
                        <Field name="firstName" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6]" />
                        <ErrorMessage name="firstName" component="div" className="text-red-500 text-xs mt-1" />
                      </div>
                      <div className="flex-1">
                        <label className="block mb-1">Last Name</label>
                        <Field name="lastName" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6]" />
                        <ErrorMessage name="lastName" component="div" className="text-red-500 text-xs mt-1" />
                      </div>
                    </div>
                    <div className="flex gap-4 flex-col md:flex-row">
                      <div className="flex-1">
                        <label className="block mb-1">Organization</label>
                        <Field name="organization" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6]" />
                        <ErrorMessage name="organization" component="div" className="text-red-500 text-xs mt-1" />
                      </div>
                      <div className="flex-1">
                        <label className="block mb-1">Email</label>
                        <Field name="email" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6]" />
                        <ErrorMessage name="email" component="div" className="text-red-500 text-xs mt-1" />
                      </div>
                    </div>
                    <div>
                      <label className="block mb-1">Address</label>
                      <Field name="addressLine1" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6] mb-2" placeholder="Address line 1 (required)" />
                      <ErrorMessage name="addressLine1" component="div" className="text-red-500 text-xs mt-1" />
                      <Field name="addressLine2" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6]" placeholder="Address line 2 (optional)" />
                      <ErrorMessage name="addressLine2" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                    <div className="flex gap-4 flex-col md:flex-row">
                      <div className="flex-1">
                        <label className="block mb-1">ZIP Code</label>
                        <Field name="zipcode" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6]" />
                        <ErrorMessage name="zipcode" component="div" className="text-red-500 text-xs mt-1" />
                      </div>
                      <div className="flex-1">
                        <label className="block mb-1">State</label>
                        <Field as="select" name="state" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6]">
                          <option value="">Select state</option>
                          {US_STATES.map((state) => (
                            <option key={state.code} value={state.name}>{state.name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="state" component="div" className="text-red-500 text-xs mt-1" />
                      </div>
                    </div>
                    <div className="flex gap-4 flex-col md:flex-row">
                      <div className="flex-1">
                        <label className="block mb-1">City</label>
                        <Field name="city" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6]" />
                        <ErrorMessage name="city" component="div" className="text-red-500 text-xs mt-1" />
                      </div>
                      <div className="flex-1">
                        <label className="block mb-1">Country</label>
                        <Field name="country" className="px-4 py-2.5 w-full rounded-[0.5rem] border-[1px] border-[#D0D0D6] bg-gray-100" disabled />
                      </div>
                    </div>
                    <div className="flex w-full justify-center items-center bg-angular-gradient rounded-full p-0.5 mt-5">
                      <button
                        type="submit"
                        className="w-full font-semibold bg-white-a700 rounded-full p-1.5 hover:bg-light-angular-gradient"
                        disabled={isSubmitting || loading}
                      >
                        {loading ? "Saving..." : "Save billing information"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const PurchasePage = () => {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { step, setStep } = usePurchaseStepStore();
  const [quantity, setQuantity] = useState(1);
  const [hasLicense, setHasLicense] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [billingCycle, setBillingCycle] = useState("1year"); // '1year' or '3year'
  const [plans, setPlans] = useState<Plan[]>([]);
  const [activeLicenses, setActiveLicenses] = useState<number>(0);
  const [isBillingModalOpen, setBillingModalOpen] = useState(false);
  const [hasBillingInfo, setHasBillingInfo] = useState(false);
  const [billingInfo, setBillingInfo] = useState<any>(null);
  const [triedToContinue, setTriedToContinue] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [finalTotal, setFinalTotal] = useState<number | null>(null);

  // fetch plans
  useEffect(() => {
    const fetchPlans = async () => {
      const serviceId = SERVICE_ID;
      try {
        const response = await networkService.get<any>(
          `${GET_PLANS}/${serviceId}/plans`
        );
        const activePlans = response.data.results.filter(
          (plan: Plan) => plan.isActive
        );
        setPlans(activePlans);
        console.log(activePlans);
      } catch (err) {
        toast.error("Failed to fetch plans");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);


  // Use intervalCount to distinguish 1 year and 3 year plans
  const oneYearPlan = plans.find(plan => (plan.pricingModel as any)?.intervalCount === 1 && plan.pricingModel.billingCycle === "CUSTOM");
  const threeYearPlan = plans.find(plan => (plan.pricingModel as any)?.intervalCount === 3 && plan.pricingModel.billingCycle === "CUSTOM");

  // Pricing logic (dynamic)
  let pricePerLicense = 0;
  let originalPricePerLicense = 0;
  let totalPrice = 0;
  let originalTotal = 0;
  let showDiscount = false;

  if (billingCycle === '1year') {
    // For 1-year plan
    pricePerLicense = oneYearPlan?.pricingModel.unitPrice || 0;
    originalPricePerLicense = oneYearPlan?.pricingModel.unitPriceOriginal || pricePerLicense;
    totalPrice = pricePerLicense * quantity;
    originalTotal = originalPricePerLicense * quantity;
    showDiscount = originalPricePerLicense > pricePerLicense;
  } else {
    // For 3-year plan
    pricePerLicense = threeYearPlan?.pricingModel.unitPrice || 0;
    originalPricePerLicense = threeYearPlan?.pricingModel.unitPriceOriginal || pricePerLicense;
    totalPrice = pricePerLicense * quantity;
    originalTotal = originalPricePerLicense * quantity;
    showDiscount = originalPricePerLicense > pricePerLicense;
  }

  // The minimum quantity is always 1
  const minQuantity = 1;
  // The maximum quantity is 5 minus the number of active licenses (but at least 1)
  const maxQuantity = Math.max(1, 5 - activeLicenses);

  const handleIncrement = () => {
    setQuantity((prev) => Math.min(maxQuantity, prev + 1));
  };

  const handleDecrement = () => {
    setQuantity((prev) => Math.max(minQuantity, prev - 1));
  };

  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: "/purchase" } });
      return;
    }
    setStep(1);
    // Fetch licenses for the user
    const fetchLicenses = async () => {
      setLoading(true);
      try {
        let url = "";
        if (user.role === UserRole.Admin) {
          url = `${GET_LICENSES_BY_ORG_ID}/${user.organizationId}?pageIndex=0&pageSize=1`;
        } else {
          url = `${GET_LICENSES_BY_ORG_ID}/${user.organizationId}`;
        }
        const response = await networkService.get<any>(url);
        const licenses = response.data.results || [];  
        // Exclude Education licenses
        const nonEducationLicenses = licenses.filter(
          (lic: any) => lic.subscription?.plan?.name?.toLowerCase() !== "education"
        );

        // If there are any non-Education licenses, user is NOT eligible for trial
        const hasNonEducationLicense = nonEducationLicenses.length > 0;
        setHasLicense(hasNonEducationLicense);

        // If user is eligible for trial, set due date 30 days from now
        if (!hasNonEducationLicense) {
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const due = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
          setDueDate(due.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }));
        }
      } catch (error) {
        handleError({ error, message: (error as Error).message, level: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchLicenses();
  }, [user, navigate, setStep]);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!user?.organizationId) return;
      try {
        const res = await getOrgStatusSummary(user.organizationId);
        setActiveLicenses(res.data.activeLicenses || 0);
        // Use paidPlanLicenses to determine free trial eligibility
        const paidPlanLicenses = res.data.paidPlanLicenses || 0;
        setHasLicense(paidPlanLicenses > 0);
        if (paidPlanLicenses === 0) {
          // Eligible for free trial
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const due = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
          setDueDate(due.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }));
        }
      } catch (e) {
        setActiveLicenses(0); // fallback
      }
    };
    fetchStatus();
  }, [user]);

  // Coupon apply handler
  const handleApplyCoupon = async () => {
    setCouponError("");
    if (!couponCode) return;
    try {
      const planId = billingCycle === "1year" ? oneYearPlan?.id : threeYearPlan?.id;
      const res = await networkService.post<any>(
        `/subscriptions/calculate-final-amount/${user.organizationId}/${planId}`,
        {
          quantity,
          discountPayload: { promotionCode: couponCode },
        }
      );
      if (res.data?.success && res.data?.pricing?.discountAmount > 0) {
        setAppliedCoupon({ code: couponCode, discount: res.data.pricing.discountAmount / 100 });
        setDiscountAmount(res.data.pricing.discountAmount / 100);
        setFinalTotal(res.data.pricing.amountDue / 100);
        setCouponError("");
        setShowCouponInput(false);
      } else {
        setCouponError("Sorry, that coupon does not exit or doesn't work anymore");
        setAppliedCoupon(null);
        setDiscountAmount(0);
        setFinalTotal(null);
      }
    } catch (e) {
      setCouponError("Sorry, that coupon does not exit or doesn't work anymore");
      setAppliedCoupon(null);
      setDiscountAmount(0);
      setFinalTotal(null);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setFinalTotal(null);
    setCouponCode("");
    setCouponError("");
    setShowCouponInput(false);
  };

  // Step 2: Stripe payment redirection logic
  const handleContinueToPayment = async () => {
    setTriedToContinue(false); // reset on each attempt
    if (!hasBillingInfo) {
      setTriedToContinue(true);
      toast.error("Billing information is required.");
      return;
    }
    try {
      setStep(2); // Move to step 2
      const organizationId = user?.organizationId;
      const planId = billingCycle === "1year"
        ? oneYearPlan?.id
        : threeYearPlan?.id;
      // 1. Create subscription
      const subscriptionResponse = await networkService.post<any>(
        `${CREATE_SUBSCRIPTION}/${organizationId}/${planId}`,
        { nickName: `Subscription to ${planId}` }
      );
      const subscriptionId = subscriptionResponse.data.subscription.id;
      // 2. Get Stripe payment URL
      const paymentBody: any = {
        successUrl: `${window.location.origin}/callback/success?subscriptionId=${subscriptionId}&sessionId={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/purchase`,
        subId: subscriptionId,
        quantity: quantity,
      };
      if (appliedCoupon) {
        paymentBody.discountPayload = { promotionCode: appliedCoupon.code };
      }
      const result = await networkService.post<any>(
        `${SUBSCRIBE_PAYMENT}/${organizationId}/${planId}`,
        paymentBody
      );

      if (result.data.url) {
        // Store the checkout session ID in localStorage before redirecting
        const checkoutSessionId = result.data.id;
        localStorage.setItem('checkoutSessionId', checkoutSessionId);
        localStorage.setItem('subscriptionId', subscriptionId);
        window.location = result.data.url;
      }
    } catch (error) {
      handleError({ error, message: (error as Error).message, level: "error" });
    }
  };

  // Function to query licenses using checkout session ID
  const queryLicenses = async (checkoutSessionId: string, subscriptionId: string) => {
    try {
      const response = await networkService.get<any>(
        `/subscriptions/query-created-licenses/${checkoutSessionId}`,
        {
          successUrl: `${window.location.origin}/order-confirmation`,
          subId: subscriptionId
        }
      );
      return response.data;
    } catch (error) {
      handleError({ error, message: (error as Error).message, level: "error" });
      return null;
    }
  };

  const customPlans = plans.filter(plan => plan.pricingModel?.billingCycle === "CUSTOM");

  // Refetch billing info utility
  const fetchBillingInfo = () => {
    if (user?.organizationId) {
      networkService.get<BillingDetailsResponse>(`/organizations/${user.organizationId}/billing-details`)
        .then(res => {
          const data = res.data;
          setHasBillingInfo(data && Object.keys(data).length > 0);
          setBillingInfo(data && Object.keys(data).length > 0 ? data : null);
        })
        .catch(() => {
          setHasBillingInfo(false);
          setBillingInfo(null);
        });
    }
  };

  useEffect(() => {
    fetchBillingInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isBillingModalOpen]);

  useEffect(() => {
    // Reset coupon UI and state when billing cycle changes
    setShowCouponInput(false);
    setCouponCode("");
    setAppliedCoupon(null);
    setCouponError("");
    setDiscountAmount(0);
    setFinalTotal(null);
  }, [billingCycle]);

  // 1-year plan
  const oneYearPricePerLicense = oneYearPlan?.pricingModel.unitPrice || 0;
  const oneYearOriginalPricePerLicense = oneYearPlan?.pricingModel.unitPriceOriginal || oneYearPricePerLicense;
  const oneYearTotal = oneYearPricePerLicense * quantity;
  const oneYearOriginalTotal = oneYearOriginalPricePerLicense * quantity;
  const oneYearShowDiscount = oneYearOriginalPricePerLicense > oneYearPricePerLicense;

  // 3-year plan
  const threeYearPricePerLicense = threeYearPlan?.pricingModel.unitPrice || 0;
  const threeYearOriginalPricePerLicense = threeYearPlan?.pricingModel.unitPriceOriginal || threeYearPricePerLicense;
  const threeYearTotal = threeYearPricePerLicense * quantity;
  const threeYearOriginalTotal = threeYearOriginalPricePerLicense * quantity;
  const threeYearShowDiscount = threeYearOriginalPricePerLicense > threeYearPricePerLicense;

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  return (
    <div className="container mx-auto md:px-4 px-10 md:py-8 py-16 font-space-grotesk">
      {/* Back Button */}
      <button
        className="flex items-center gap-2 text-gray-700 hover:text-black md:mb-6 mb-11"
        onClick={() => navigate("/licenses")}
      >
        <ArrowLeft size={20} className="mr-3" />
        <span>Back</span>
      </button>

      {/* Progress Steps */}
      <StepCounter step={step} />

      {/* Heading and Subheading */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2">Tailor our offer to your needs</h1>
        <p className="md:text-xl text-gray-600 text-base mt-3">Simply choose what's best for you.</p>
      </div>

      <div className="bg-white rounded-lg md:px-32 md:w-[1300px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Product Selection */}
          <div className="flex-1">
            {/* Trial Banner (conditional) */}
            {!hasLicense && (
              <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-md text-indigo-700 text-sm mb-6">
                <div className="flex gap-2">
                  <div>
                    🎉
                  </div>
                  <div>
                    <strong>We offer you a 30-day Free Trial!</strong>
                    <p className="text-indigo-600 mt-1">You will be automatically charged on <strong>{new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>. We will send you a reminder before your trial ends.</p>
                  </div>
                </div>
              </div>
            )}
            {/* Product Info */}
            <div className="flex items-center justify-between gap-10 border border-gray-300 rounded-md p-3 px-5 mb-4">
              <div>
                <h3 className="text-lg font-semibold">HeartFocus License</h3>
                <ul className="text-sm mt-2 space-y-1 z-50">
                  <li className="flex items-center gap-2">
                    <LavendarChevronIcon width={6} height={10} />
                    <span className="ml-2">Access for 1 probe to HeartFocus</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <LavendarChevronIcon width={6} height={10} />
                    <span className="ml-2">Compatible only with Butterfly iQ+ and iQ3</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <LavendarChevronIcon width={6} height={10} />
                    <span className="ml-2">Available on iPad only</span>
                  </li>
                </ul>
              </div>
              <div className="relative hidden md:flex items-center justify-center mr-8">
                <div
                  className="absolute rounded-full bg-angular-gradient w-[180px] h-[120px] pt-14"
                  style={{
                    opacity: 0.5,
                    filter: 'blur(20px)'
                  }}
                />
                <img
                  src="/license-purchase-ultrasound.svg"
                  alt="No licenses"
                  className="relative z-10 w-[120px] h-[120px] object-contain"
                />
              </div>
            </div>
            {/* License Selector */}
            {quantity + activeLicenses === 5 && (
              <div className="bg-green-50 border border-green-300 p-4 rounded-md mb-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <strong className="text-green-800">Need more than 5 licenses? Let's talk! </strong>
                  <a
                    href="https://www.heartfocus.ai/#book-demo"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:block ml-2 font-semibold underline text-green-900"
                  >
                    Contact sales
                  </a>
                </div>
                <span className="block text-green-700">
                  Purchases over 5 licenses require an Enterprise plan. Our<br />
                  team will help you find the best fit.
                </span>
                <a
                  href="https://www.heartfocus.ai/#book-demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:hidden block font-semibold underline text-green-900"
                >
                  Contact sales
                </a>
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={handleDecrement}
                className="border px-3 py-1 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={quantity <= minQuantity}
              >
                -
              </button>
              <span className="px-3 border py-1 border-gray-300 rounded-md">{quantity} license{quantity !== 1 ? 's' : ''}</span>
              <button
                onClick={handleIncrement}
                disabled={quantity >= maxQuantity}
                className="border px-3 py-1 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>

            {/* Billing Cycle */}
            <div className="mt-4">
              <h4 className="font-semibold text-gray-700 mb-2">Billing cycle</h4>
              <div className="flex flex-row gap-3">
                {/* 1 Year Option */}
                {oneYearPlan && (
                  <div className={`flex-1 rounded-md transition-all ${billingCycle === '1year' ? 'bg-angular-gradient p-0.5' : 'p-0.5 bg-transparent'}`}>
                    <label
                      className={`flex md:items-center flex-col md:flex-row md:justify-between justify-start gap-3 ${billingCycle === '1year' ? 'bg-white-a700 border border-transparent' : 'bg-white-a700 border border-gray-300'} rounded-md px-4 py-4 cursor-pointer w-full transition-all`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="billing"
                          checked={billingCycle === '1year'}
                          onChange={() => setBillingCycle('1year')}
                          className="hidden"
                        />
                        <span className="w-6 h-6 flex items-center justify-center">
                          {/* Outer gradient border */}
                          <span
                            className={`w-6 h-6 flex items-center justify-center rounded-full p-[2.5px] ${billingCycle === '1year' ? 'bg-gradient-to-br from-[#E9E5DE] to-[#F2AE8B]' : 'bg-gray-300'}`}
                          >
                            {/* Inner white ring */}
                            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white-a700">
                              {/* Inner dot (only if selected) */}
                              {billingCycle === '1year' && (
                                <span className="w-3 h-3 rounded-full bg-gradient-to-br from-[#E9E5DE] to-[#F2AE8B] block"></span>
                              )}
                            </span>
                          </span>
                        </span>
                        <div className="text-base font-medium w-full flex flex-col md:flex-row justify-between">
                          <span>1 year</span>
                        </div>
                      </div>
                      <div>
                        <span className="flex items-center gap-2">
                          {oneYearShowDiscount && (
                            <span className="line-through text-gray-500">${oneYearOriginalPricePerLicense}</span>
                          )}
                          <span className="font-semibold text-base">${oneYearPricePerLicense}</span>
                        </span>
                      </div>
                    </label>
                  </div>
                )}
                {/* 3 Year Option */}
                {threeYearPlan && (
                  <div className={`flex-1 rounded-md transition-all ${billingCycle === '3year' ? 'bg-angular-gradient p-0.5' : 'p-0.5 bg-transparent'}`}>
                    <label
                      className={`flex md:items-center flex-col md:flex-row md:justify-between justify-start gap-3 ${billingCycle === '3year' ? 'bg-white-a700 border border-transparent' : 'bg-white-a700 border border-gray-300'} rounded-md px-4 py-4 cursor-pointer w-full transition-all relative`}
                    >
                      <div className="flex items-center gap-2">

                        <input
                          type="radio"
                          name="billing"
                          checked={billingCycle === '3year'}
                          onChange={() => setBillingCycle('3year')}
                          className="hidden"
                        />
                        <span className="w-6 h-6 flex items-center justify-center">
                          {/* Outer gradient border */}
                          <span
                            className={`w-6 h-6 flex items-center justify-center rounded-full p-[2.5px] ${billingCycle === '3year' ? 'bg-gradient-to-br from-[#E9E5DE] to-[#F2AE8B]' : 'bg-gray-300'}`}
                          >
                            {/* Inner white ring */}
                            <span className="w-full h-full flex items-center justify-center rounded-full bg-white-a700">
                              {/* Inner dot (only if selected) */}
                              {billingCycle === '3year' && (
                                <span className="w-3 h-3 rounded-full bg-gradient-to-br from-[#E9E5DE] to-[#F2AE8B] block"></span>
                              )}
                            </span>
                          </span>
                        </span>
                        <div className="text-sm font-medium w-full flex flex-col md:flex-row justify-between">
                          <span>3 years</span>
                        </div>
                      </div>
                      <div>
                        <span className="flex items-center gap-2">
                          {threeYearShowDiscount && (
                            <span className="line-through text-gray-500">${threeYearOriginalPricePerLicense}</span>
                          )}
                          <span className="font-semibold">${threeYearPricePerLicense}</span>
                        </span>
                      </div>
                      <span className="absolute -top-2 right-8 text-xs bg-black-900 text-white-a700 px-2 py-0.5 rounded-full">Save 15%</span>
                    </label>
                  </div>
                )}
              </div>
            </div>


            {/* Billing Info Placeholder */}
            <div className="mt-5">
              <h4 className="font-semibold text-gray-700 mb-2">Billing information</h4>
              {hasBillingInfo && billingInfo ? (
                <div className="flex flex-col border border-gray-300 rounded-md px-5 py-4 text-base font-medium bg-white mb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="mb-1">{billingInfo.name || ""}</div>
                      <div className="font-semibold text-base mb-1">{billingInfo.legalName || ""}</div>
                      <div className="mb-1">{billingInfo.addressLine1 || ""}{billingInfo.addressLine2 && `, ${billingInfo.addressLine2}`}</div>
                      <div className="mb-1">
                        {billingInfo.city || ""}
                        {billingInfo.state ? `, ${US_STATES.find(s => s.name === billingInfo.state || s.code === billingInfo.state)?.code || billingInfo.state}` : ""}
                        {billingInfo.zipcode ? ` ${billingInfo.zipcode}` : ""}
                      </div>
                      <div className="mb-1">United States</div>
                    </div>
                    <button className="text-sm underline ml-4" onClick={() => setBillingModalOpen(true)}>Edit</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className={`flex items-center justify-between px-3 py-4 rounded-md text-sm font-medium border ${(!hasBillingInfo && triedToContinue) ? 'border-[#F87171] shadow-[0_0_6px_1.5px_#F87171B2]' : 'border-gray-300'} transition-all`}>
                    <p>No billing information</p>
                    <button className="text-sm underline" onClick={() => setBillingModalOpen(true)}>Add info</button>
                  </div>
                  {(!hasBillingInfo && triedToContinue) && (
                    <div className="flex items-center gap-2 mt-2 text-[#F87171] text-sm">
                      <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#F87171" strokeWidth="2" /><path d="M12 8v4m0 4h.01" stroke="#F87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      <span className="text-[13px]">Oops! We need your billing address before we can finalize your license purchase.</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="h-[1px] w-full md:hidden bg-gray-300 my-3"></div>

          {/* Right: Summary Panel */}
          <div className="w-full lg:w-[460px] rounded-xl space-y-6">
            {/* 3-year subscription savings banner */}
            {billingCycle === '1year' && (
              <div className="w-full mb-2">
                <div className="flex items-center bg-[#F2EFEE] border border-[#D6CECB] rounded-md px-4 py-5 text-base text-gray-700 font-medium">
                  <span className="mr-2">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.25135 18L4.69459 15.2288L1.67838 14.5932L1.97027 11.3898L0 9L1.97027 6.61017L1.67838 3.4322L4.69459 2.77119L6.25135 0L9 1.24576L11.7486 0L13.3297 2.77119L16.3459 3.4322L16.0297 6.61017L18 9L16.0297 11.3898L16.3459 14.5932L13.3297 15.2288L11.7486 18L9 16.7542L6.25135 18Z" fill="#756660" />
                      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" font-size="7" font-weight="bold" fill="white" font-family="Arial, sans-serif">%</text>
                    </svg>
                  </span>
                  <span>Switch to a 3-year subscription to <span className="text-[#756660] font-semibold">save 15%.</span></span>
                </div>
              </div>
            )}
            <div className="space-y-4 border border-gray-300 rounded-md p-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Billing summary</h3>
              </div>
              <div className="text-sm">
                <div className="flex justify-between py-2">
                  <span>HeartFocus License x {quantity}</span>
                  <span className="font-medium flex items-center gap-2">
                    {billingCycle === '1year' && oneYearShowDiscount && (
                      <span className="line-through text-gray-500">${oneYearOriginalTotal.toLocaleString()}</span>
                    )}
                    {billingCycle === '1year' && (
                      <span className="font-semibold">${oneYearTotal.toLocaleString()}</span>
                    )}
                    {billingCycle === '3year' && (
                      <span className="font-semibold">${threeYearTotal.toLocaleString()}</span>
                    )}
                  </span>
                </div>
                <div className="text-gray-500">
                  <span>{billingCycle === '1year' ? '1 year' : '3 years'}</span>
                  <span className="float-right">{!hasLicense && 'Free for 30 days'}</span>
                </div>
                {/* Coupon UI */}
                {!appliedCoupon && !showCouponInput && (
                  <button className="text-indigo-600 text-sm underline mt-2" onClick={() => setShowCouponInput(true)}>Add coupon code</button>
                )}
                {!appliedCoupon && showCouponInput && (
                  <div className="flex flex-col gap-2 mt-4">
                    <label htmlFor="coupon-code" className="text-sm font-medium">Coupon</label>
                    <div className="flex items-center justify-between gap-2">
                      <input
                        type="text"
                        className={`w-full outline-none border rounded px-2 py-2 text-sm ${couponError ? 'border-red-400' : 'border-gray-300'}`}
                        placeholder="Enter code"
                        value={couponCode}
                        onChange={e => setCouponCode(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleApplyCoupon(); }}
                        autoFocus
                      />
                      <button
                        className="px-4 py-2 rounded-full border border-gray-300 text-white text-sm font-semibold"
                        onClick={handleApplyCoupon}
                        type="button"
                      >Apply</button>
                    </div>
                  </div>
                )}
                {couponError && (
                  <div className="text-xs text-red-500 mt-1">{couponError}</div>
                )}
                {appliedCoupon && (
                  <div className="flex items-center gap-2 mt-4 justify-between">
                    <span className="bg-[#F2EFEE] text-[#544B48] px-2 py-2 rounded text-xs font-semibold flex items-center gap-1">
                      {appliedCoupon.code}
                      <button className="ml-1 text-[#544B48] hover:text-red-500" onClick={handleRemoveCoupon} type="button"><X size={14} /></button>
                    </span>
                    <span className="text-black-900 font-semibold">-${discountAmount.toLocaleString()}</span>
                  </div>
                )}
              </div>
              <hr />
              <div className="text-sm">
                {!hasLicense && (
                  <div className="flex justify-between">
                    <span>Due on {dueDate}</span>
                    <span className="font-medium flex items-center gap-2">
                      {appliedCoupon && finalTotal !== null
                        ? (
                          <>
                            {billingCycle === '3year' && (
                              <span className="line-through text-gray-500">${threeYearTotal.toLocaleString()}</span>
                            )}
                            <span className="font-semibold">${finalTotal.toLocaleString()}</span>
                          </>
                        )
                        : billingCycle === '3year'
                          ? (
                            <>
                              <span className="font-semibold">${threeYearTotal.toLocaleString()}</span>
                            </>
                          )
                          : <span className="font-medium">${oneYearTotal.toLocaleString()}</span>
                      }
                    </span>
                  </div>
                )}
                <div className={`flex justify-between${hasLicense ? " mt-0" : " mt-1"}`}>
                  <span>Due today</span>
                  {hasLicense ? (
                    <span className="font-semibold text-gray-700">
                      {appliedCoupon && finalTotal !== null
                        ? `$${finalTotal.toLocaleString()}`
                        : billingCycle === '3year'
                          ? `$${threeYearTotal.toLocaleString()}`
                          : `$${oneYearTotal.toLocaleString()}`
                      }
                    </span>
                  ) : (
                    <span className="font-semibold text-green-600">$0</span>
                  )}
                </div>
              </div>
              <div className="flex w-full justify-center items-center bg-angular-gradient rounded-full p-0.5 mt-5">
                <button
                  type="submit"
                  className="w-full font-semibold bg-white-a700 rounded-full p-1.5 hover:bg-light-angular-gradient"
                  disabled={loading}
                  onClick={handleContinueToPayment}>
                  Continue to Payment
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                {hasLicense
                  ? "Your subscription will start today."
                  : `Your subscription will start on ${dueDate}, unless you cancel. You can cancel at any time.`}
              </p>
            </div>
          </div>
        </div>
      </div>
      <BillingInfoModal
        isOpen={isBillingModalOpen}
        onClose={() => setBillingModalOpen(false)}
        onSaved={() => {
          setBillingModalOpen(false);
          fetchBillingInfo();
        }}
        user={user}
        isFirstTime={!hasBillingInfo} // Pass this prop
      />
    </div>
  );
};

export default PurchasePage; 